import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCompetitions, checkUserCanManageCompetitions, deleteCompetition } from '../../services/competitionService';
import { getAgeGroups } from '../../services/ageGroupService';
import { getActiveSportsYear, getSportsYears } from '../../services/sportsYearService';
import { Competition, AgeGroup } from '../../types/models';
import { Search, Plus, Calendar, Layers, File as FilePdf, Trash2, ChevronLeft, ChevronRight, Database, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { exportAllCompetitionsToPDF, exportAllResultsToPDF } from '../../utils/export';
import { supabase } from '../../lib/supabase';

const ITEMS_PER_PAGE = 15;

const CompetitionList: React.FC = () => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<string>('All');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [selectedYear, setSelectedYear] = useState<string>('All');
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [canManageCompetitions, setCanManageCompetitions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const { t } = useTranslation('common');
  const { user } = useAuthStore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [competitionsData, ageGroupsData, canManage, sportsYears, activeYear] = await Promise.all([
          getCompetitions(),
          getAgeGroups(),
          checkUserCanManageCompetitions(),
          getSportsYears().catch(() => []),
          getActiveSportsYear().catch(() => null),
        ]);
        setCompetitions(competitionsData);
        setAgeGroups(ageGroupsData);
        setCanManageCompetitions(canManage);

        const yearsFromData = Array.from(
          new Set(competitionsData.map(c => c.competition_year).filter(Boolean))
        ) as number[];
        const yearsFromSettings = sportsYears.map(y => y.year);
        const mergedYears = Array.from(new Set([...yearsFromSettings, ...yearsFromData]))
          .sort((a, b) => b - a);
        setAvailableYears(mergedYears);

        if (activeYear && mergedYears.includes(activeYear)) {
          setSelectedYear(activeYear.toString());
        }
        
        // Check if user is admin
        if (user) {
          const { data: userRoles } = await supabase
            .from('user_roles')
            .select(`
              roles!inner(
                role_name
              )
            `)
            .eq('user_id', user.id);
          
          setIsAdmin(userRoles?.some(ur => ur.roles.role_name === 'admin') ?? false);
          setIsManager(userRoles?.some(ur => ur.roles.role_name === 'manager') ?? false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load competitions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGender, selectedAgeGroup, selectedStatus, selectedYear]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('competitions.delete.confirmInitial'))) {
      return;
    }

    try {
      // Check if competition has registrations
      const { data: registrations } = await supabase
        .from('student_event_registrations')
        .select('registration_id')
        .eq('event_id', id);

      if (registrations && registrations.length > 0) {
        // Ask for confirmation to delete registrations
        if (!window.confirm(t('competitions.delete.confirmWithRegistrations'))) {
          return;
        }
      }

      setError(null);
      await deleteCompetition(id);
      setCompetitions(prev => prev.filter(comp => comp.event_id !== id));
    } catch (err: any) {
      console.error('Error deleting competition:', err);
      setError(err.message || 'Failed to delete competition');
    }
  };

  const filteredCompetitions = competitions.filter(competition => {
    const matchesSearch = 
      competition.event_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      competition.category?.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (competition.reference_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGender = selectedGender === 'All' || competition.gender_category === selectedGender;
    
    const matchesAgeGroup = selectedAgeGroup === 'All' || competition.age_group_id === selectedAgeGroup;
    
    const matchesStatus = selectedStatus === 'All' || competition.status === selectedStatus;

    const matchesYear = selectedYear === 'All' || competition.competition_year?.toString() === selectedYear;

    return matchesSearch && matchesGender && matchesAgeGroup && matchesStatus && matchesYear;
  });

  const totalPages = Math.ceil(filteredCompetitions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCompetitions = filteredCompetitions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleUpdateAllStatuses = async (status: Competition['status']) => {
    if (!window.confirm(`Are you sure you want to update all filtered competitions to "${status}" status?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get IDs of all filtered competitions
      const competitionIds = filteredCompetitions.map(comp => comp.event_id);
      
      // Update all competitions in batches
      for (let i = 0; i < competitionIds.length; i += 10) {
        const batch = competitionIds.slice(i, i + 10);
        
        const { error } = await supabase
          .from('events')
          .update({ status })
          .in('event_id', batch);
          
        if (error) throw error;
      }
      
      // Refresh competitions data
      const updatedCompetitions = await getCompetitions();
      setCompetitions(updatedCompetitions);
      
    } catch (error: any) {
      console.error('Error updating competition statuses:', error);
      setError(error.message || 'Failed to update competition statuses');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('competitions.title')}</h1>
        <div className="mt-3 md:mt-0 flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-3">
          <button
            onClick={() => exportAllResultsToPDF(filteredCompetitions)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FilePdf className="h-4 w-4 mr-2" />
            {t('competitions.actions.exportAllResults')}
          </button>
          <button
            onClick={() => exportAllCompetitionsToPDF(filteredCompetitions)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FilePdf className="h-4 w-4 mr-2" />
            {t('competitions.actions.exportAllScoreSheets')}
          </button>
          {user && canManageCompetitions && (
            <>
              {(isAdmin || isManager) && (
                <div className="relative group">
                  <button
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('competitions.actions.updateAllStatus')}
                  </button>
                  <div className="hidden group-hover:block absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                    <div className="py-1">
                      <button
                        onClick={() => handleUpdateAllStatuses('not_started')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('competitions.status.not_started')}
                      </button>
                      <button
                        onClick={() => handleUpdateAllStatuses('in_progress')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('competitions.status.in_progress')}
                      </button>
                      <button
                        onClick={() => handleUpdateAllStatuses('completed')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('competitions.status.completed')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {(isAdmin || isManager) && (
                <Link
                  to="/competitions/admin"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Database className="h-4 w-4 mr-2" />
                  {t('competitions.actions.adminView')}
                </Link>
              )}
              <Link
                to="/competitions/categories"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Layers className="h-4 w-4 mr-2" />
                {t('competitions.actions.manageCategories')}
              </Link>
              <Link
                to="/competitions/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('competitions.actions.addCompetition')}
              </Link>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('competitions.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allGenders')}</option>
                <option value="M">{t('competitions.filters.male')}</option>
                <option value="F">{t('competitions.filters.female')}</option>
                <option value="A">{t('competitions.filters.all')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedAgeGroup}
                onChange={(e) => setSelectedAgeGroup(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allAgeGroups')}</option>
                {ageGroups.map(group => (
                  <option key={group.age_group_id} value={group.age_group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allStatuses')}</option>
                <option value="not_started">{t('competitions.status.not_started')}</option>
                <option value="in_progress">{t('competitions.status.in_progress')}</option>
                <option value="completed">{t('competitions.status.completed')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">All years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y.toString()}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredCompetitions.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('competitions.noCompetitions.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedGender !== 'All' || selectedAgeGroup !== 'All'
                ? t('competitions.noCompetitions.description.withFilters')
                : t('competitions.noCompetitions.description.noFilters')}
            </p>
            {user && canManageCompetitions && (
              <div className="mt-6">
                <Link
                  to="/competitions/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('competitions.actions.addCompetition')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.table.eventName')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.table.referenceNumber')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.table.category')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.table.ageGroup')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.table.gender')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('competitions.table.status')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    {user && canManageCompetitions && (
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">{t('competitions.table.actions')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedCompetitions.map((competition) => {
                    const ageGroup = ageGroups.find(g => g.age_group_id === competition.age_group_id);
                    return (
                      <tr key={competition.event_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link to={`/competitions/${competition.event_id}`}>
                            <div className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer">
                              {competition.event_name}
                            </div>
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {competition.reference_number}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {competition.category?.category_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {ageGroup ? ageGroup.group_name : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            competition.gender_category === 'M' ? 'bg-blue-100 text-blue-800' :
                            competition.gender_category === 'F' ? 'bg-pink-100 text-pink-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {competition.gender_category === 'M' ? t('competitions.filters.male') :
                             competition.gender_category === 'F' ? t('competitions.filters.female') :
                             t('competitions.filters.all')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            competition.status === 'not_started' ? 'bg-gray-100 text-gray-800' :
                            competition.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {t(`competitions.status.${competition.status}`)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {competition.competition_year ?? '-'}
                          </span>
                        </td>
                        {user && canManageCompetitions && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-3">
                              <Link to={`/competitions/${competition.event_id}`} className="text-blue-600 hover:text-blue-800">
                                {t('competitions.actions.view')}
                              </Link>
                              <Link to={`/competitions/${competition.event_id}/edit`} className="text-indigo-600 hover:text-indigo-800">
                                {t('competitions.actions.edit')}
                              </Link>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDelete(competition.event_id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(startIndex + ITEMS_PER_PAGE, filteredCompetitions.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredCompetitions.length}</span> results
                  </p>
                </div>
                {totalPages > 1 && (
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompetitionList;